import { IPromise } from 'angular';
import * as React from 'react';
import { Messages } from '../../../codegen/Localize';
import { RadioButtonWithLabelOption, RadioButtonWithLabelGroupWidget, InlineAlertWidget, InlineAlertType } from '@tableau/widgets';
import { ICollectionInfo } from './ICollectionInfo';
import { CollectionVisibility, UpdateCollectionsResponseData } from './CollectionModels';
import { CollectionsResourceQuery } from './Collections';
import { cssClass } from '../../../style';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { TabDialogWidth } from '../../../commonInfra/dialogs/style/TabDialogWidth';
import { openReactActionDialog, ReactActionDialogBodyProps } from '../../../commonInfra/dialogs/ReactActionDialog';

// Requires knowledge of inner InlineAlertWidget setup
// Currently widget places a -16px margin botton which doesn't play nice when the
// alert is shown; the dialog doesn't properly expand vertically to include
// the bottom margin cause a scroll bar to appear.
// Overflow is hidden on the dialog body in case this does regress so at least
// no scroll bar will show but it'll be missing the bottom margin
const inlineAlertContentContainer = cssClass('prefix', 'container', {
  $nest: {
    [`.contentContainer`]: {
      marginBottom: '0px'
    }
  }
});

const radioButtonOptions = (): RadioButtonWithLabelOption<String>[] => {
  return [
    {
      value: 'private',
      testId: 'collection-visibility-private',
      label: Messages.collectionVisibilityPrivateRadioButton()
    },
    {
      value: 'public',
      testId: 'collection-visibility-public',
      label: Messages.collectionVisibilityPublicRadioButton()
    }
  ];
};


export type CollectionVisibilityDialogBodyProps = {
  readonly originalVisibility: CollectionVisibility;
  readonly collectionName: string;
};
export type CollectionVisibilityDialogState = {
  readonly visibility: CollectionVisibility;
};
export type PropsForConfirmActionDialogBody = ReactActionDialogBodyProps<CollectionVisibilityDialogBodyProps, CollectionVisibilityDialogState>;

const CollectionVisibilityDialogBody = (props: PropsForConfirmActionDialogBody) => {
  const [ isPrivate, setPrivate ] = React.useState(props.originalVisibility === 'private');
  const handleSelect = (value: string) => {
    if (value === 'private') {
      props.updateDialogState({visibility: CollectionVisibility.private});
      setPrivate(true);
    } else {
      props.updateDialogState({visibility: CollectionVisibility.public});
      setPrivate(false);
    }
  };
  return (
    <div style={{overflowY: 'hidden'}}>
      <div>
        {Messages.collectionsVisibilityDialogHeader({COLLECTION_NAME: props.collectionName})}
      </div>
      <RadioButtonWithLabelGroupWidget
        aria-label={Messages.collectionsVisibilityDialogTitle()}
        buttonList={radioButtonOptions()}
        checkedIndex={isPrivate ? 0 : 1}
        handleChange={handleSelect}
      />
      {isPrivate && props.originalVisibility === 'public' &&
        <InlineAlertWidget
          className={inlineAlertContentContainer}
          testId={'collections-visibility'}
          type={InlineAlertType.Warning}
          style={{ marginTop: '8px'}}
        >
          {Messages.collectionsVisibilityDialogAlert()}
        </InlineAlertWidget>
      }
    </div>
  );
};

export namespace ChangeCollectionVisibilityDialog {
  export function open(collection: ICollectionInfo): IPromise<any> {
    const modalInstance = openReactActionDialog<CollectionVisibilityDialogBodyProps, CollectionVisibilityDialogState, any>({
      title:  Messages.collectionsVisibilityDialogTitle(),
      bodyComponent: CollectionVisibilityDialogBody,
      initialBodyProps: {
        originalVisibility: collection.visibility,
        collectionName: collection.name
      },
      initialDialogState: {
        visibility: collection.visibility
      },
      actionName: Messages.save(),
      action: async (_bodyProps: CollectionVisibilityDialogBodyProps, dialogState: CollectionVisibilityDialogState) => {
        const updatedCollectionData = {
          luid: collection.luid,
          visibility: dialogState.visibility
        };
        return new CollectionsResourceQuery().update([updatedCollectionData])
          .then((response: UpdateCollectionsResponseData) => {
            if (response.errors && response.errors.length > 0) {
              popErrorToastForChangingVisibility(dialogState);
              return;
            }
            ToasterService.instance.pop(
              {
                type: ToastType.Success,
                text: dialogState.visibility === 'private'?
                  Messages.collectionVisibilityChangedToPrivateSuccess() : Messages.collectionVisibilityChangedToPublicSuccess(),
                testId: 'change-collection-visibility-success'
              }
            );
          })
          .catch(() => {
            popErrorToastForChangingVisibility(dialogState);
          });
      },
      actionDisabled: (bodyProps: CollectionVisibilityDialogBodyProps, dialogState: CollectionVisibilityDialogState) => {
        return dialogState.visibility === bodyProps.originalVisibility;
      },
      dialogWidth: TabDialogWidth.width500px,
      dismissDialogOnActionFailed: false,
    });

    return modalInstance.result;
  }

  function popErrorToastForChangingVisibility(dialogState: CollectionVisibilityDialogState) {
    ToasterService.instance.pop(
      {
        type: ToastType.Error,
        text: dialogState.visibility === 'private' ?
          Messages.collectionVisibilityChangedToPrivateError() : Messages.collectionVisibilityChangedToPublicError(),
        testId: 'change-collection-visibility-success'
      }
    );
  }
}
